import React, { useState, useCallback, useEffect } from 'react';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { logger } from 'src/utils/logger';
import { parseTaskOutput } from 'src/utils/CommonUtils';
import retry from 'async-retry';

interface UseTaskOutputProps {
	workflowId: string;
	workflowExecutionId: string;
}

interface TaskData {
	taskName: string;
	taskId?: string;
}

const TASK_OUTPUT_RETRY_CONFIG = {
	retries: 2,
	minTimeout: 500,
	maxTimeout: 2000,
	factor: 2,
} as const;

export const useTaskOutput = ({ workflowId, workflowExecutionId }: UseTaskOutputProps) => {
	const [taskOutput, setTaskOutput] = useState<string | object>('');
	const [isLoadingTaskOutput, setIsLoadingTaskOutput] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortControllerRef = React.useRef<AbortController | null>(null);

	const {
		onClickCB: fetchTaskOutput,
	} = useFetchOnUserActionUsingSDS<{ taskOutput?: string }>();

	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	const fetchTaskOutputData = useCallback(async (task: TaskData) => {
		if (!task.taskName?.trim()) {
			const errorMessage = 'Task name is required to fetch task output';

			logger.error(errorMessage, new Error(errorMessage));
			setTaskOutput('Error loading task output');
			setError(errorMessage);

			return;
		}

		const taskName = task.taskName;
		
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		
		// Create new AbortController for this request
		const abortController = new AbortController();

		abortControllerRef.current = abortController;
		
		setIsLoadingTaskOutput(true);
		setTaskOutput('');
		setError(null);
		
		try {
			logger.info(`Calling fetchTaskOutput API for ${taskName} - ${task.taskId}`);
			
			const response = await retry(
				async (bail) => {
					try {
						const result = await fetchTaskOutput({
							path: `/workflows/${workflowId}/executions/${workflowExecutionId}/output`,
							method: 'GET',
							url: SNOW_NXT_WORKFLOW_ENDPOINT,
							queryParams: {
								taskNames: taskName,
								_t: Date.now(),
							},
							signal: abortController.signal,
						});
						
						if (result?.error) {
							logger.info(`API returned error, will retry for ${taskName}: ${result.error}`);
							throw new Error(result.error);
						}
						
						return result;
					} catch (error: unknown) {
						if (error instanceof Error && error.name === 'AbortError') {
							bail(error);

							return;
						}

						throw error;
					}
				},
				{
					...TASK_OUTPUT_RETRY_CONFIG,
					onRetry: (error, attempt) => {
						logger.info(`Retry attempt ${attempt} for ${taskName} - ${task.taskId}`);
					},
				}
			);

			if (abortController.signal.aborted) {
				logger.info(`Request aborted for ${taskName} - ${task.taskId}`);

				return;
			}

			logger.info(`API Response received: ${taskName} - ${task.taskId}`);

			const hasOutput = response?.taskExecutionsOutput && 
			                  Object.prototype.hasOwnProperty.call(response.taskExecutionsOutput, taskName);
			
			if (hasOutput) {
				const rawOutput = response?.taskExecutionsOutput[taskName];
				
				if (rawOutput === null || rawOutput === undefined || rawOutput === '') {
					logger.info(`Task Output is empty/null for ${taskName} - ${task.taskId}`);
					setTaskOutput('No output available');
				} else {
					const parsedOutput = parseTaskOutput(rawOutput);

					setTaskOutput(parsedOutput);
				}
			} else {
				logger.info(`No output key found in response for ${taskName} - ${task.taskId}`);
				setTaskOutput('No output available');
			}
		} catch (error: unknown) {
			if (error instanceof Error && error.name === 'AbortError') {
				logger.info(`Request cancelled for ${taskName} - ${task.taskId}`);

				return;
			}

			const errorMessage = `Error occurred while fetching Task output ${taskName} - ${task.taskId}`;

			if (error instanceof Error) {
				logger.error(errorMessage, error);
				setError(error.message);
			} else {
				logger.error(errorMessage, new Error(String(error)));
				setError(String(error));
			}

			setTaskOutput('Error loading task output');
		} finally {
			if (!abortController.signal.aborted) {
				setIsLoadingTaskOutput(false);
			}
		}
	}, [fetchTaskOutput, workflowId, workflowExecutionId]);

	const resetTaskOutput = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}

		setTaskOutput('');
		setIsLoadingTaskOutput(false);
		setError(null);
	}, []);

	return {
		taskOutput,
		isLoadingTaskOutput,
		error,
		fetchTaskOutputData,
		resetTaskOutput,
	};
};
